/**
 * Agent text segment renderer with inline markdown support.
 * Shared across all agent timeline views.
 */

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { Database, ChevronDown } from "lucide-react";
import { type TextSegmentType, tryFormatJson } from "../shared";
import { renderInlineMarkdown } from "./AgentTextSegment.helpers";

export function AgentTextSegment({ segment }: { segment: TextSegmentType }) {
  switch (segment.type) {
    case "heading":
      return (
        <p className="mt-2 text-xs font-semibold text-foreground first:mt-0">
          {renderInlineMarkdown(segment.text)}
        </p>
      );
    case "prose":
      return (
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/80">
          {renderInlineMarkdown(segment.text)}
        </p>
      );
    case "list_item":
      return (
        <div className="flex items-start gap-1.5 pl-1 text-xs text-foreground/80">
          <span className="mt-0.5 shrink-0 text-muted-foreground">&bull;</span>
          <span>{renderInlineMarkdown(segment.text)}</span>
        </div>
      );
    case "json_output":
      return (
        <Collapsible>
          <CollapsibleTrigger className="flex w-fit cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground">
            <Database className="h-3 w-3" />
            <span className="font-medium">Structured output</span>
            <ChevronDown className="h-3 w-3" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/30 p-2 font-mono text-[10px] leading-relaxed">
              {tryFormatJson(segment.json)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      );
    case "phase_marker":
      return (
        <div className="flex items-center gap-2 py-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
            {segment.phase}
          </span>
          <div className="flex-1 border-t border-border/40" />
        </div>
      );
    default:
      return null;
  }
}
